/**
 * AddReferenceModal.tsx
 *
 * Build Bar — Step 3: Images — Reference image picker modal.
 *
 * Lets the user select one or more reference images from four sources:
 *   1. Logo       — the client logo uploaded in the Setup bar.
 *   2. Generadas  — images already generated in this wizard session.
 *   3. Products   — the global base-product catalog (~55 transparent cut-outs).
 *   4. Upload     — a drag-and-drop / click-to-upload area for custom files.
 *
 * Selection is multi-select; each image gets a visible selection border.
 * "Confirm" calls `onSelect` with the chosen `ReferenceImage[]`.
 *
 * This is a self-contained modal — it does NOT use the global ModalBase /
 * useModalStore because it needs a controlled open/close prop from the parent
 * so the parent can react to the selected images synchronously.
 */

import { useState, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';

import { api, extractApiError } from '@/lib/axios';
import { Button, Skeleton } from '@/components/ui';
import { useBaseProducts } from '@/api/useWizardV2';
import type { ReferenceImage, BaseProduct } from '@/types/wizardV2';
import styles from './AddReferenceModal.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A generated-image entry passed in from the parent. */
export interface GeneratedImageEntry {
  /** GCS URL of the generated image. */
  url: string;
  /** 0-based index of the slide that produced this image. */
  slideIndex: number;
}

export interface AddReferenceModalProps {
  /** Whether the modal is visible. */
  isOpen: boolean;
  /** Called when the user dismisses the modal (X button or overlay click). */
  onClose: () => void;
  /**
   * Called when the user confirms their selection.
   * Receives the full `ReferenceImage[]` for all selected images.
   */
  onSelect: (images: ReferenceImage[]) => void;
  /** GCS URL of the client logo, or null/undefined if not set. */
  logoUrl?: string | null;
  /** All images generated so far in this wizard session. */
  generatedImages: GeneratedImageEntry[];
}

/** Tab identifiers. */
type ModalTab = 'logo' | 'generadas' | 'productos' | 'subir';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Tab label map. */
const TAB_LABELS: Record<ModalTab, string> = {
  logo: 'Logo',
  generadas: 'Generated',
  productos: 'Base Products',
  subir: 'Upload',
};

const TAB_ORDER: ModalTab[] = ['logo', 'generadas', 'productos', 'subir'];

/** Accepted MIME types for the upload tab. */
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * A single thumbnail in the picker grid.
 * Shows a selection ring when `isSelected` is true.
 */
function PickerThumb({
  url,
  label,
  isSelected,
  onToggle,
}: {
  url: string;
  label: string;
  isSelected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.thumb} ${isSelected ? styles.thumbSelected : ''}`}
      onClick={onToggle}
      aria-label={`${isSelected ? 'Deselect' : 'Select'} ${label}`}
      aria-pressed={isSelected}
      title={label}
    >
      <img src={url} alt={label} className={styles.thumbImg} />
      {isSelected && <span className={styles.thumbCheck} aria-hidden>&#10003;</span>}
    </button>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * AddReferenceModal — four-tab reference image picker.
 *
 * Renders a portal-style modal overlay directly (no global ModalBase) so the
 * parent receives the confirmed selection synchronously via `onSelect`.
 *
 * Internal selection state is managed with a `Map<id, ReferenceImage>` so
 * adding images from different tabs merges cleanly.
 */
export const AddReferenceModal = ({
  isOpen,
  onClose,
  onSelect,
  logoUrl,
  generatedImages,
}: AddReferenceModalProps) => {
  // ── Tab navigation ─────────────────────────────────────────────────────────

  const [activeTab, setActiveTab] = useState<ModalTab>('logo');

  // ── Selection state ────────────────────────────────────────────────────────

  /**
   * Map from reference image `id` to its `ReferenceImage` object.
   * Using a Map preserves insertion order and enables O(1) toggle.
   */
  const [selected, setSelected] = useState<Map<string, ReferenceImage>>(new Map());

  const toggleRef = useCallback((image: ReferenceImage) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(image.id)) {
        next.delete(image.id);
      } else {
        next.set(image.id, image);
      }
      return next;
    });
  }, []);

  const isSelected = useCallback(
    (id: string) => selected.has(id),
    [selected],
  );

  // ── Base-products query ────────────────────────────────────────────────────

  const { data: baseProducts, isLoading: productsLoading } = useBaseProducts();

  // ── Product search / filter ────────────────────────────────────────────────

  const [productSearch, setProductSearch] = useState('');

  const filteredProducts = (baseProducts ?? []).filter(
    (p) =>
      productSearch === '' ||
      p.name_es.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.category.toLowerCase().includes(productSearch.toLowerCase()),
  );

  /** Group products by category. */
  const productsByCategory = filteredProducts.reduce<Record<string, BaseProduct[]>>(
    (acc, p) => {
      const cat = p.category;
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(p);
      return acc;
    },
    {},
  );

  // ── Upload tab state ───────────────────────────────────────────────────────

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<ReferenceImage[]>([]);

  /**
   * Upload a file to `POST /api/providers/upload-image/` and add it to the
   * local `uploadedImages` list, then auto-select it.
   */
  const handleUpload = useCallback(
    async (file: File) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast.error('Only PNG, JPEG, or WebP images are accepted.', { duration: 5_000 });
        return;
      }

      setIsUploading(true);

      try {
        const formData = new FormData();
        formData.append('image', file);

        const res = await api.post<{ url: string }>('/api/providers/upload-image/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const imageId = `uploaded-${Date.now()}-${file.name}`;
        const refImage: ReferenceImage = {
          id: imageId,
          url: res.data.url,
          source: 'uploaded',
        };

        setUploadedImages((prev) => [...prev, refImage]);
        // Auto-select the freshly uploaded image.
        setSelected((prev) => {
          const next = new Map(prev);
          next.set(refImage.id, refImage);
          return next;
        });

        toast.success('Image uploaded successfully.', { duration: 3_000 });
      } catch (err) {
        toast.error(extractApiError(err, 'Could not upload the image. Please try again.'), { duration: 8_000 });
      } finally {
        setIsUploading(false);
      }
    },
    [],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void handleUpload(file);
      // Reset so the same file can be re-selected.
      e.target.value = '';
    },
    [handleUpload],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDraggingOver(false);
      const file = e.dataTransfer.files[0];
      if (file) void handleUpload(file);
    },
    [handleUpload],
  );

  // ── Confirm selection ──────────────────────────────────────────────────────

  const handleConfirm = useCallback(() => {
    onSelect(Array.from(selected.values()));
    onClose();
  }, [selected, onSelect, onClose]);

  // ── Overlay dismiss ────────────────────────────────────────────────────────

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  // ── Guard: nothing to render when closed ──────────────────────────────────

  if (!isOpen) return null;

  // ── Tab content renderers ──────────────────────────────────────────────────

  const renderTabContent = () => {
    switch (activeTab) {
      // ── Logo tab ──────────────────────────────────────────────────────────
      case 'logo':
        return (
          <div className={styles.tabContent}>
            {logoUrl ? (
              <div className={styles.logoSection}>
                <p className={styles.tabHint}>
                  Select the client logo to use it as a reference.
                </p>
                <PickerThumb
                  url={logoUrl}
                  label="Client logo"
                  isSelected={isSelected('logo')}
                  onToggle={() =>
                    toggleRef({
                      id: 'logo',
                      url: logoUrl,
                      source: 'logo',
                    })
                  }
                />
              </div>
            ) : (
              <div className={styles.emptyState}>
                <p>No logo available.</p>
                <p className={styles.emptyHint}>
                  Upload the client logo in the Details step of Setup.
                </p>
              </div>
            )}
          </div>
        );

      // ── Generadas tab ──────────────────────────────────────────────────────
      case 'generadas':
        return (
          <div className={styles.tabContent}>
            {generatedImages.length === 0 ? (
              <div className={styles.emptyState}>
                <p>You have not generated any images in this session yet.</p>
                <p className={styles.emptyHint}>
                  Images you generate will appear here so you can reuse them.
                </p>
              </div>
            ) : (
              <div className={styles.thumbGrid}>
                {generatedImages.map((img, i) => {
                  const id = `generated-${img.slideIndex}-${i}`;
                  return (
                    <PickerThumb
                      key={id}
                      url={img.url}
                      label={`Slide ${img.slideIndex + 1}`}
                      isSelected={isSelected(id)}
                      onToggle={() =>
                        toggleRef({
                          id,
                          url: img.url,
                          source: 'generated',
                        })
                      }
                    />
                  );
                })}
              </div>
            )}
          </div>
        );

      // ── Productos Base tab ────────────────────────────────────────────────
      case 'productos':
        return (
          <div className={styles.tabContent}>
            <div className={styles.searchRow}>
              <input
                type="search"
                className={styles.searchInput}
                placeholder="Search product..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                aria-label="Search base product"
              />
            </div>

            {productsLoading ? (
              <div className={styles.skeletonGrid}>
                {Array.from({ length: 12 }, (_, i) => (
                  <Skeleton key={i} variant="rect" width={60} height={60} />
                ))}
              </div>
            ) : Object.keys(productsByCategory).length === 0 ? (
              <div className={styles.emptyState}>
                <p>No products found matching your search.</p>
              </div>
            ) : (
              <div className={styles.categoryList}>
                {Object.entries(productsByCategory).map(([category, products]) => (
                  <div key={category} className={styles.categoryGroup}>
                    <h3 className={styles.categoryLabel}>{category}</h3>
                    <div className={styles.thumbGrid}>
                      {products.map((product) => {
                        const id = `base-product-${product.slug}`;
                        return (
                          <PickerThumb
                            key={product.slug}
                            url={product.base_image_url}
                            label={product.name_es}
                            isSelected={isSelected(id)}
                            onToggle={() =>
                              toggleRef({
                                id,
                                url: product.base_image_url,
                                source: 'base_product',
                                productSlug: product.slug,
                              })
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      // ── Subir tab ──────────────────────────────────────────────────────────
      case 'subir':
        return (
          <div className={styles.tabContent}>
            {/* Drop zone */}
            <div
              className={`${styles.dropZone} ${isDraggingOver ? styles.dropZoneDragOver : ''} ${isUploading ? styles.dropZoneUploading : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDraggingOver(true);
              }}
              onDragLeave={() => setIsDraggingOver(false)}
              onDrop={handleDrop}
              onClick={() => !isUploading && fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              aria-label="Image upload area"
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !isUploading) {
                  fileInputRef.current?.click();
                }
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES.join(',')}
                className={styles.fileInput}
                onChange={handleFileInputChange}
                aria-hidden
                tabIndex={-1}
              />

              {isUploading ? (
                <p className={styles.dropZoneText}>Uploading image...</p>
              ) : (
                <>
                  <p className={styles.dropZoneText}>
                    Drag an image here or click to select
                  </p>
                  <p className={styles.dropZoneHint}>PNG, JPEG or WebP</p>
                </>
              )}
            </div>

            {/* Uploaded images grid */}
            {uploadedImages.length > 0 && (
              <div className={styles.uploadedSection}>
                <h3 className={styles.categoryLabel}>Uploaded images</h3>
                <div className={styles.thumbGrid}>
                  {uploadedImages.map((img) => (
                    <PickerThumb
                      key={img.id}
                      url={img.url}
                      label="Uploaded image"
                      isSelected={isSelected(img.id)}
                      onToggle={() => toggleRef(img)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Select reference images"
    >
      <div className={styles.modal}>
        {/* ── Header ───────────────────────────────────────────────────────── */}
        <div className={styles.header}>
          <h2 className={styles.title}>Add references</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close modal"
          >
            &#10005;
          </button>
        </div>

        {/* ── Tab bar ──────────────────────────────────────────────────────── */}
        <div className={styles.tabBar} role="tablist">
          {TAB_ORDER.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              className={`${styles.tabButton} ${activeTab === tab ? styles.tabButtonActive : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>

        {/* ── Tab panel ────────────────────────────────────────────────────── */}
        <div className={styles.panelArea} role="tabpanel">
          {renderTabContent()}
        </div>

        {/* ── Footer ───────────────────────────────────────────────────────── */}
        <div className={styles.footer}>
          <span className={styles.selectionCount} aria-live="polite" aria-atomic>
            {selected.size > 0
              ? `${selected.size} image${selected.size !== 1 ? 's' : ''} selected`
              : 'None selected'}
          </span>
          <div className={styles.footerActions}>
            <Button variant="secondary" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleConfirm}
              disabled={selected.size === 0}
            >
              Confirm
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
